import React, { useState, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { supabase } from "../../supabaseClient";
import LoadingButton from "../LoadingButton/LoadingButton";
import { OrbitLoaderComponent } from "../../utils/orbitLoader";
import { useNotifications } from "../../components/NotificationContext/NotificationContext";
import "./PermissionsContent.css";

const PermissionsContent = () => {
  const location = useLocation();
  const { addNotification } = useNotifications();
  const [users, setUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState("all");
  const [selectAll, setSelectAll] = useState(false);
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    permission_type: "",
    start_date: "",
    end_date: "",
    reason: "",
    is_direct: true,
  });
  const [formLoading, setFormLoading] = useState(false);
  const [notification, setNotification] = useState({
    show: false,
    message: "",
    type: "",
  });
  const [activeTab, setActiveTab] = useState("pending");
  const [applyingFilters, setApplyingFilters] = useState(false);
  const [editingPermissionId, setEditingPermissionId] = useState(null);
  const [showEmployeeSelector, setShowEmployeeSelector] = useState(false);
  const [modalEmployeeSelector, setModalEmployeeSelector] = useState(false);
  const [employeeSearchTerm, setEmployeeSearchTerm] = useState("");
  const [modalEmployeeSearchTerm, setModalEmployeeSearchTerm] = useState("");
  const filterRef = useRef(null);
  const modalRef = useRef(null);

  useEffect(() => {
    fetchUsers();
    fetchPermissions();

    const handleClickOutside = (event) => {
      if (filterRef.current && !filterRef.current.contains(event.target)) {
        setShowEmployeeSelector(false);
      }
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        setModalEmployeeSelector(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    fetchPermissions();
  }, [selectedUser, activeTab]);

  const fetchUsers = async () => {
    try {
      const { data, error } = await supabase
        .from("users")
        .select("user_id, name, surname")
        .eq("is_active", true)
        .order("name");
      if (!error && data) {
        setUsers(data);
        if (location.state?.userId) {
          setSelectedUser(location.state.userId);
          setSelectedUsers([location.state.userId]);
        }
      }
    } catch (error) {
      console.error("Kullanıcılar yüklenemedi:", error.message);
    }
  };

  const fetchPermissions = async () => {
    setLoading(true);
    try {
      let query = supabase
        .from("permissions")
        .select(
          `
          *,
          users(name, surname)
        `
        )
        .order("created_at", { ascending: false });
      if (activeTab !== "all") {
        query = query.eq("status", activeTab);
      }
      if (selectedUser !== "all") {
        query = query.eq("user_id", selectedUser);
      }
      const { data, error } = await query;
      if (!error) {
        setPermissions(data || []);
      } else {
        console.error("İzinler yüklenemedi:", error.message);
        setPermissions([]);
      }
    } catch (error) {
      console.error("İzinler yüklenemedi:", error.message);
      setPermissions([]);
    } finally {
      setLoading(false);
    }
  };

  const handleFormChange = (e) => {
    const { name, value, type } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? e.target.checked : value,
    }));
  };

  const handleFilterEmployeeSelect = (userId) => {
    setSelectedUser(userId);
    setShowEmployeeSelector(false);
    setEmployeeSearchTerm("");
  };

  const handleModalEmployeeSelect = (userId) => {
    if (userId === "all") {
      setSelectedUsers(users.map((user) => user.user_id));
      setSelectAll(true);
    } else {
      if (selectedUsers.includes(userId)) {
        setSelectedUsers((prev) => prev.filter((id) => id !== userId));
      } else {
        setSelectedUsers((prev) => [...prev, userId]);
      }
      setSelectAll(false);
    }
    setModalEmployeeSearchTerm("");
  };

  const handleUserSelection = (userId) => {
    if (selectAll) {
      setSelectAll(false);
      setSelectedUsers([]);
    } else {
      setSelectedUsers((prev) => {
        if (prev.includes(userId)) {
          return prev.filter((id) => id !== userId);
        } else {
          return [...prev, userId];
        }
      });
    }
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectAll(false);
      setSelectedUsers([]);
    } else {
      setSelectAll(true);
      setSelectedUsers(users.map((user) => user.user_id));
    }
  };

  const handleUserChange = (e) => {
    const userId = e.target.value;
    setSelectedUser(userId);
    if (userId === "all") {
      setSelectAll(true);
      setSelectedUsers(users.map((user) => user.user_id));
    } else {
      setSelectAll(false);
      setSelectedUsers([userId]);
    }
  };

  const applyFilters = () => {
    setApplyingFilters(true);
    fetchPermissions();
    setTimeout(() => {
      setApplyingFilters(false);
      setNotification({
        show: true,
        message: "Veriler başarıyla yenilendi!",
        type: "success",
      });
      setTimeout(() => {
        setNotification({ show: false, message: "", type: "" });
      }, 3000);
    }, 1000);
  };

  const resetForm = () => {
    setFormData({
      permission_type: "",
      start_date: "",
      end_date: "",
      reason: "",
      is_direct: true,
    });
    setSelectAll(false);
    setSelectedUsers([]);
    setEditingPermissionId(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    try {
      if (editingPermissionId) {
        const { error } = await supabase
          .from("permissions")
          .update({
            permission_type: formData.permission_type,
            start_date: formData.start_date,
            end_date: formData.end_date,
            reason: formData.reason,
            status: formData.is_direct ? "approved" : "pending",
          })
          .eq("permission_id", editingPermissionId);
        if (error) throw error;
        const permission = permissions.find(
          (p) => p.permission_id === editingPermissionId
        );
        if (permission) {
          addNotification({
            type: "permission_update",
            icon: "fa-edit",
            title: "İzin Talebiniz Güncellendi",
            message: `${formData.permission_type} talebiniz güncellendi`,
            targetUserId: permission.user_id,
            route: "/employee/leave-request",
          });
        }
        setNotification({
          show: true,
          message: "İzin başarıyla güncellendi",
          type: "success",
        });
      } else {
        if (!selectAll && selectedUsers.length === 0) {
          setNotification({
            show: true,
            message: "Lütfen en az bir personel seçin",
            type: "error",
          });
          setFormLoading(false);
          return;
        }
        let targetUsers = [];
        if (selectAll) {
          const { data: allUsers, error } = await supabase
            .from("users")
            .select("user_id")
            .eq("is_active", true);
          if (error) throw error;
          targetUsers = allUsers.map((user) => user.user_id);
        } else {
          targetUsers = selectedUsers;
        }
        const permissionsToInsert = targetUsers.map((userId) => ({
          user_id: userId,
          permission_type: formData.permission_type,
          start_date: formData.start_date,
          end_date: formData.end_date,
          reason: formData.reason,
          status: formData.is_direct ? "approved" : "pending",
        }));
        const { data, error } = await supabase
          .from("permissions")
          .insert(permissionsToInsert);
        if (error) throw error;
        if (formData.is_direct) {
          targetUsers.forEach((userId) => {
            addNotification({
              type: "permission_direct",
              icon: "fa-check-circle",
              title: "İzin Onaylandı",
              message: `${formData.permission_type} talebiniz onaylandı`,
              targetUserId: userId,
              route: "/employee/leave-request",
            });
          });
        } else {
          addNotification({
            type: "permission_request",
            icon: "fa-user-clock",
            title: "Yeni İzin Talepleri",
            message: `${targetUsers.length} personel için izin talebi oluşturuldu`,
            targetRole: "admin",
            route: "/admin/permissions",
          });
        }
        setNotification({
          show: true,
          message: `${targetUsers.length} personel için izin ${
            formData.is_direct ? "oluşturuldu" : "talebi gönderildi"
          }`,
          type: "success",
        });
      }
      setShowModal(false);
      fetchPermissions();
      resetForm();
    } catch (error) {
      setNotification({
        show: true,
        message: editingPermissionId
          ? "İzin güncellenemedi: " + error.message
          : "İzin oluşturulamadı: " + error.message,
        type: "error",
      });
    } finally {
      setFormLoading(false);
      setTimeout(() => {
        setNotification({ show: false, message: "", type: "" });
      }, 3000);
    }
  };

  const updatePermissionStatus = async (permissionId, status) => {
    try {
      const { error } = await supabase
        .from("permissions")
        .update({ status })
        .eq("permission_id", permissionId);
      if (error) throw error;
      const permission = permissions.find(
        (p) => p.permission_id === permissionId
      );
      if (permission) {
        addNotification({
          type: "permission",
          icon: status === "approved" ? "fa-check-circle" : "fa-times-circle",
          title: `İzin Talebiniz ${
            status === "approved" ? "Onaylandı" : "Reddedildi"
          }`,
          message: `${permission.permission_type} talebiniz ${
            status === "approved" ? "onaylandı" : "reddedildi"
          }`,
          targetUserId: permission.user_id,
          route: "/employee/leave-request",
        });
        addNotification({
          type: "permission_update",
          icon: status === "approved" ? "fa-check-circle" : "fa-times-circle",
          title: `İzin Talebi ${
            status === "approved" ? "Onaylandı" : "Reddedildi"
          }`,
          message: `${permission.users?.name} ${
            permission.users?.surname
          } adlı çalışanın ${permission.permission_type} talebi ${
            status === "approved" ? "onaylandı" : "reddedildi"
          }`,
          targetRole: "admin",
          route: "/admin/permissions",
        });
      }
      setNotification({
        show: true,
        message: `İzin ${status === "approved" ? "onaylandı" : "reddedildi"}`,
        type: "success",
      });
      fetchPermissions();
    } catch (error) {
      setNotification({
        show: true,
        message: "İzin durumu güncellenemedi: " + error.message,
        type: "error",
      });
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { class: "perm-status-pending", text: "Bekliyor" },
      approved: { class: "perm-status-approved", text: "Onaylandı" },
      rejected: { class: "perm-status-rejected", text: "Reddedildi" },
    };
    const config = statusConfig[status] || statusConfig.pending;
    return (
      <span className={`perm-status-badge ${config.class}`}>{config.text}</span>
    );
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("tr-TR");
  };

  const getSelectedUserName = () => {
    if (!selectedUser) return "";
    if (selectedUser === "all") return "Tüm Personeller";
    const user = users.find((u) => u.user_id === selectedUser);
    return user ? `${user.name} ${user.surname}` : "";
  };

  const getSelectedUsersText = () => {
    if (selectAll) return "Tüm Personeller";
    if (selectedUsers.length === 0) return "Personel Seçiniz";
    if (selectedUsers.length === 1) {
      const user = users.find((u) => u.user_id === selectedUsers[0]);
      return user ? `${user.name} ${user.surname}` : "";
    }
    return `${selectedUsers.length} Personel Seçildi`;
  };

  const getFilteredUsers = () => {
    if (!employeeSearchTerm) return users;
    return users.filter((user) =>
      `${user.name} ${user.surname}`
        .toLowerCase()
        .includes(employeeSearchTerm.toLowerCase())
    );
  };

  const getModalFilteredUsers = () => {
    if (!modalEmployeeSearchTerm) return users;
    return users.filter((user) =>
      `${user.name} ${user.surname}`
        .toLowerCase()
        .includes(modalEmployeeSearchTerm.toLowerCase())
    );
  };

  const getFilteredPermissions = () => {
    return permissions;
  };

  if (loading) {
    return (
      <div className="perm-loading-container">
        <OrbitLoaderComponent size="medium" showPath={true} />
        <p className="loading-text">Yükleniyor...</p>
      </div>
    );
  }

  const filteredUsers = getFilteredUsers();
  const modalFilteredUsers = getModalFilteredUsers();
  const filteredPermissions = getFilteredPermissions();

  return (
    <div className="perm-content">
      <div className="perm-header">
        <div className="perm-header-info">
          <h2>İzin Yönetimi</h2>
          <p>Personel izin taleplerini oluşturun ve yönetin</p>
        </div>
        <div className="perm-header-actions">
          <button
            className="perm-btn perm-btn-primary"
            onClick={() => {
              resetForm();
              setShowModal(true);
            }}
          >
            <span className="perm-btn-icon">
              <i className="fas fa-plus"></i>
            </span>
            <span className="perm-btn-text">Yeni İzin</span>
          </button>
        </div>
      </div>
      {notification.show && (
        <div
          className={`perm-notification perm-notification-${notification.type}`}
        >
          {notification.message}
        </div>
      )}
      <div className="filter-section">
        <div className="filter-group">
          <div className="filter-item" ref={filterRef}>
            <label htmlFor="user-select">Personel:</label>
            <div className="user-select">
              <button
                className="perm-btn perm-btn-primary"
                onClick={() => setShowEmployeeSelector(!showEmployeeSelector)}
              >
                {getSelectedUserName() || "Personel Seç"}
                <i className="fas fa-chevron-down filter-btn-icon"></i>
              </button>
              {showEmployeeSelector && (
                <div className="work-schedule-employee-selector">
                  <div className="work-employee-selector-header">
                    <div className="work-employee-selector-icon">
                      <i className="fas fa-users"></i>
                    </div>
                    <h3 className="work-employee-selector-title">
                      Personel Seçimi
                    </h3>
                  </div>
                  <div className="work-employee-search-container">
                    <div className="work-employee-search-wrapper">
                      <input
                        type="text"
                        className="work-employee-search-input"
                        placeholder="Personel ara..."
                        value={employeeSearchTerm}
                        onChange={(e) => setEmployeeSearchTerm(e.target.value)}
                      />
                      <i className="fas fa-search work-employee-search-icon"></i>
                    </div>
                  </div>
                  <div className="work-employee-list">
                    <div
                      className={`work-employee-list-item ${
                        selectedUser === "all" ? "selected" : ""
                      }`}
                      onClick={() => handleFilterEmployeeSelect("all")}
                    >
                      <div className="work-employee-avatar">
                        <i className="fas fa-users"></i>
                      </div>
                      <div className="work-employee-info">
                        <div className="work-employee-name">
                          Tüm Personeller
                        </div>
                        <div className="work-employee-role">Genel Ayarlar</div>
                      </div>
                      <div className="work-employee-check">
                        <i className="fas fa-check"></i>
                      </div>
                    </div>
                    {filteredUsers.map((user) => (
                      <div
                        key={user.user_id}
                        className={`work-employee-list-item ${
                          selectedUser === user.user_id ? "selected" : ""
                        }`}
                        onClick={() => handleFilterEmployeeSelect(user.user_id)}
                      >
                        <div className="work-employee-avatar">
                          {user.name.charAt(0)}
                          {user.surname.charAt(0)}
                        </div>
                        <div className="work-employee-info">
                          <div className="work-employee-name">
                            {user.name} {user.surname}
                          </div>
                          <div className="work-employee-role">Personel</div>
                        </div>
                        <div className="work-employee-check">
                          <i className="fas fa-check"></i>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className="filter-actions">
            <button
              onClick={applyFilters}
              disabled={applyingFilters}
              className="filter-apply-btn"
            >
              {applyingFilters ? (
                <>
                  <i className="fas fa-spinner fa-spin"></i> Yükleniyor...
                </>
              ) : (
                <>
                  <i className="fas fa-sync-alt"></i> Yenile
                </>
              )}
            </button>
          </div>
        </div>
      </div>
      <div className="perm-tabs">
        <button
          className={`perm-tab ${
            activeTab === "pending" ? "perm-tab-active" : ""
          }`}
          onClick={() => setActiveTab("pending")}
        >
          <i className="fas fa-clock perm-tab-icon"></i>
          <span className="perm-tab-text">Bekleyen</span>
        </button>
        <button
          className={`perm-tab ${
            activeTab === "approved" ? "perm-tab-active" : ""
          }`}
          onClick={() => setActiveTab("approved")}
        >
          <i className="fas fa-check-circle perm-tab-icon"></i>
          <span className="perm-tab-text">Onaylanan</span>
        </button>
        <button
          className={`perm-tab ${
            activeTab === "rejected" ? "perm-tab-active" : ""
          }`}
          onClick={() => setActiveTab("rejected")}
        >
          <i className="fas fa-times-circle perm-tab-icon"></i>
          <span className="perm-tab-text">Reddedilen</span>
        </button>
      </div>
      <div className="perm-table-container">
        <table className="perm-table">
          <thead>
            <tr>
              <th>Personel</th>
              <th>İzin Türü</th>
              <th>Başlangıç</th>
              <th>Bitiş</th>
              <th>Sebep</th>
              <th>Durum</th>
              <th>İşlemler</th>
            </tr>
          </thead>
          <tbody>
            {filteredPermissions.length > 0 ? (
              filteredPermissions.map((permission) => (
                <tr key={permission.permission_id}>
                  <td>
                    <div className="perm-user-info">
                      <i className="fas fa-user perm-user-icon"></i>
                      <span className="perm-user-name">
                        {permission.users?.name} {permission.users?.surname}
                      </span>
                    </div>
                  </td>
                  <td className="perm-type">{permission.permission_type}</td>
                  <td className="perm-date">
                    {formatDate(permission.start_date)}
                  </td>
                  <td className="perm-date">
                    {formatDate(permission.end_date)}
                  </td>
                  <td className="perm-reason">{permission.reason}</td>
                  <td>{getStatusBadge(permission.status)}</td>
                  <td className="perm-actions">
                    {permission.status === "pending" && (
                      <>
                        <button
                          className="perm-btn-icon perm-btn-approve"
                          onClick={() =>
                            updatePermissionStatus(
                              permission.permission_id,
                              "approved"
                            )
                          }
                          title="Onayla"
                        >
                          <i className="fas fa-check"></i>
                        </button>
                        <button
                          className="perm-btn-icon perm-btn-reject"
                          onClick={() =>
                            updatePermissionStatus(
                              permission.permission_id,
                              "rejected"
                            )
                          }
                          title="Reddet"
                        >
                          <i className="fas fa-times"></i>
                        </button>
                      </>
                    )}
                    <button
                      className="perm-btn-icon perm-btn-edit"
                      onClick={() => {
                        setEditingPermissionId(permission.permission_id);
                        setSelectedUsers([permission.user_id]);
                        setSelectedUser(permission.user_id);
                        setFormData({
                          permission_type: permission.permission_type,
                          start_date: permission.start_date,
                          end_date: permission.end_date,
                          reason: permission.reason,
                          is_direct: permission.status === "approved",
                        });
                        setShowModal(true);
                      }}
                      title="Düzenle"
                    >
                      <i className="fas fa-edit"></i>
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="7" className="perm-no-data">
                  <i className="fas fa-calendar-times perm-no-data-icon"></i>
                  <p className="perm-no-data-text">
                    {selectedUser === "all"
                      ? activeTab === "pending"
                        ? "Henüz bekleyen izin talebi bulunmuyor"
                        : activeTab === "approved"
                        ? "Henüz onaylanmış izin bulunmuyor"
                        : "Henüz reddedilmiş izin bulunmuyor"
                      : `Seçili personel için ${
                          activeTab === "pending"
                            ? "bekleyen"
                            : activeTab === "approved"
                            ? "onaylanmış"
                            : "reddedilmiş"
                        } izin bulunmuyor`}
                  </p>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {showModal && (
        <div className="perm-modal-overlay">
          <div className="perm-modal-content" ref={modalRef}>
            <div className="perm-modal-header">
              <h3 className="perm-modal-title">
                {editingPermissionId ? "İzin Düzenle" : "Yeni İzin Oluştur"}
              </h3>
              <button
                onClick={() => {
                  setShowModal(false);
                  resetForm();
                }}
                className="perm-close-button"
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="perm-modal-body">
              <form onSubmit={handleSubmit}>
                {!editingPermissionId && (
                  <div className="perm-form-group">
                    <label className="perm-form-label">Personel Seçimi</label>
                    <div className="perm-user-selector-modern">
                      <div className="perm-user-selector-button">
                        <button
                          type="button"
                          className={`perm-btn perm-btn-primary perm-user-selector-btn ${
                            modalEmployeeSelector ? "active" : ""
                          }`}
                          onClick={(e) => {
                            e.preventDefault();
                            setModalEmployeeSelector(!modalEmployeeSelector);
                          }}
                        >
                          <span className="perm-btn-text">
                            {getSelectedUsersText()}
                          </span>
                          <i
                            className={`fas fa-chevron-down filter-btn-icon ${
                              modalEmployeeSelector ? "rotated" : ""
                            }`}
                          ></i>
                        </button>
                      </div>
                      {modalEmployeeSelector && (
                        <div className="work-schedule-employee-selector">
                          <div className="work-employee-selector-header">
                            <div className="work-employee-selector-icon">
                              <i className="fas fa-users"></i>
                            </div>
                            <h3 className="work-employee-selector-title">
                              Personel Seçimi
                            </h3>
                          </div>
                          <div className="work-employee-search-container">
                            <div className="work-employee-search-wrapper">
                              <input
                                type="text"
                                className="work-employee-search-input"
                                placeholder="Personel ara..."
                                value={modalEmployeeSearchTerm}
                                onChange={(e) =>
                                  setModalEmployeeSearchTerm(e.target.value)
                                }
                              />
                              <i className="fas fa-search work-employee-search-icon"></i>
                            </div>
                          </div>
                          <div className="work-employee-list">
                            <div
                              className={`work-employee-list-item ${
                                selectAll ? "selected" : ""
                              }`}
                              onClick={() => handleModalEmployeeSelect("all")}
                            >
                              <div className="work-employee-avatar">
                                <i className="fas fa-users"></i>
                              </div>
                              <div className="work-employee-info">
                                <div className="work-employee-name">
                                  Tüm Personeli Seç
                                </div>
                                <div className="work-employee-role">
                                  Genel Ayarlar
                                </div>
                              </div>
                              <div className="work-employee-check">
                                <i className="fas fa-check"></i>
                              </div>
                            </div>
                            {modalFilteredUsers.map((user) => (
                              <div
                                key={user.user_id}
                                className={`work-employee-list-item ${
                                  selectedUsers.includes(user.user_id)
                                    ? "selected"
                                    : ""
                                }`}
                                onClick={() =>
                                  handleModalEmployeeSelect(user.user_id)
                                }
                              >
                                <div className="work-employee-avatar">
                                  {user.name.charAt(0)}
                                  {user.surname.charAt(0)}
                                </div>
                                <div className="work-employee-info">
                                  <div className="work-employee-name">
                                    {user.name} {user.surname}
                                  </div>
                                  <div className="work-employee-role">
                                    Personel
                                  </div>
                                </div>
                                <div className="work-employee-check">
                                  <i className="fas fa-check"></i>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                    {selectedUsers.length > 0 && (
                      <div className="perm-selected-users-container">
                        <div className="perm-selected-users-label">
                          Seçili Personeller:
                        </div>
                        <div className="perm-selected-users-list">
                          {selectedUsers.map((userId) => {
                            const user = users.find(
                              (u) => u.user_id === userId
                            );
                            return user ? (
                              <div
                                key={userId}
                                className="perm-selected-user-tag"
                              >
                                <span>
                                  {user.name} {user.surname}
                                </span>
                                <button
                                  type="button"
                                  className="perm-remove-user-btn"
                                  onClick={() =>
                                    handleModalEmployeeSelect(userId)
                                  }
                                >
                                  <i className="fas fa-times"></i>
                                </button>
                              </div>
                            ) : null;
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                )}
                <div className="perm-form-group">
                  <label className="perm-form-label">İzin Türü</label>
                  <select
                    name="permission_type"
                    value={formData.permission_type}
                    onChange={handleFormChange}
                    required
                    className="perm-form-select"
                  >
                    <option value="">Seçiniz</option>
                    <option value="Yıllık İzin">Yıllık İzin</option>
                    <option value="Hastalık İzni">Hastalık İzni</option>
                    <option value="Ücretsiz İzin">Ücretsiz İzin</option>
                    <option value="Evlilik İzni">Evlilik İzni</option>
                    <option value="Doğum İzni">Doğum İzni</option>
                    <option value="Ölüm İzni">Ölüm İzni</option>
                    <option value="Diğer">Diğer</option>
                  </select>
                </div>
                {formData.permission_type === "Diğer" && (
                  <div className="perm-form-group">
                    <label className="perm-form-label">İzin Adı</label>
                    <input
                      type="text"
                      name="custom_permission_type"
                      value={formData.custom_permission_type || ""}
                      onChange={handleFormChange}
                      className="perm-form-input"
                      placeholder="Özel izin adını girin"
                    />
                  </div>
                )}
                <div className="perm-form-row">
                  <div className="perm-form-group">
                    <label className="perm-form-label">Başlangıç Tarihi</label>
                    <input
                      type="date"
                      name="start_date"
                      value={formData.start_date}
                      onChange={handleFormChange}
                      required
                      className="perm-form-input"
                    />
                  </div>
                  <div className="perm-form-group">
                    <label className="perm-form-label">Bitiş Tarihi</label>
                    <input
                      type="date"
                      name="end_date"
                      value={formData.end_date}
                      onChange={handleFormChange}
                      required
                      className="perm-form-input"
                    />
                  </div>
                </div>
                <div className="perm-form-group">
                  <label className="perm-form-label">Sebep</label>
                  <textarea
                    name="reason"
                    value={formData.reason}
                    onChange={handleFormChange}
                    rows="3"
                    required
                    className="perm-form-textarea"
                  ></textarea>
                </div>
                <div className="perm-form-group">
                  <label className="perm-checkbox-label">
                    <input
                      type="checkbox"
                      name="is_direct"
                      checked={formData.is_direct}
                      onChange={handleFormChange}
                    />
                    Doğrudan İzin Oluştur (Onay Gerekmez)
                  </label>
                  <small className="form-text">
                    İşaretli bırakırsanız izin talebi onay bekler,
                    işaretlerseniz izin doğrudan onaylanır.
                  </small>
                </div>
                <div className="perm-modal-footer">
                  <button
                    type="button"
                    onClick={() => {
                      setShowModal(false);
                      resetForm();
                    }}
                    className="perm-btn perm-btn-secondary"
                  >
                    İptal
                  </button>
                  <LoadingButton
                    type="submit"
                    className="perm-btn perm-btn-primary"
                    isLoading={formLoading}
                  >
                    {editingPermissionId
                      ? "Güncelle"
                      : formData.is_direct
                      ? "Oluştur"
                      : "Talep Gönder"}
                  </LoadingButton>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PermissionsContent;
